import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import MaterialPicker from '../components/MaterialPicker';

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const niceDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const EMPTY = { product_id: '', name: '', start_date: '', end_date: '', is_active: true, choices: [{ product_id: '', offer_price: '0' }] };

const S = {
  root: { fontFamily: "'Manrope', system-ui, sans-serif", color: '#231a33', padding: '16px 20px 40px', maxWidth: 1100 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  h1: { fontFamily: "'Bricolage Grotesque','Manrope',sans-serif", fontSize: 28, margin: '0 0 4px' },
  muted: { color: '#776e86', fontSize: 14, margin: 0 },
  panel: { padding: 16, border: '1px solid #ece6f2', borderRadius: 16, background: '#fff', marginTop: 14 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#776e86' },
  input: { height: 42, padding: '0 10px', border: '1px solid #e2dcea', borderRadius: 10, font: 'inherit', fontSize: 15, background: '#fff', color: '#231a33' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 },
  btn: { height: 42, padding: '0 16px', border: '1px solid #ece6f2', borderRadius: 10, background: '#fff', font: 'inherit', fontSize: 14, fontWeight: 800, cursor: 'pointer', color: '#231a33' },
  primary: { background: '#5b3fd9', borderColor: '#5b3fd9', color: '#fff' },
  link: { border: 'none', background: 'none', padding: '4px 6px', font: 'inherit', fontSize: 13, fontWeight: 800, color: '#5b3fd9', cursor: 'pointer' },
  card: (live) => ({ padding: 14, border: `1px solid ${live ? '#cdbffb' : '#ece6f2'}`, borderRadius: 14, background: live ? '#faf8ff' : '#fff', opacity: live ? 1 : 0.7 }),
  chip: (free) => ({ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: free ? '#e6f6ee' : '#fff3e2', color: free ? '#12905f' : '#b86a00', fontWeight: 800, fontSize: 13 }),
};

function ProductSelect({ products, value, onChange, placeholder }) {
  // Reuse the searchable A-Z picker; show the price where the unit normally goes
  const items = useMemo(
    () => products.map((p) => ({ id: p.id, name: p.name, unit: rupees(p.selling_price), category: p.category })),
    [products]
  );
  return <MaterialPicker materials={items} value={value} onChange={onChange} placeholder={placeholder} />;
}

export default function ProductOffersPage() {
  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get('/product-offers'), api.get('/products')])
      .then(([o, p]) => { setOffers(o); setProducts(p.filter((x) => x.pricing_type !== 'weighted' && x.is_active !== 0)); })
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setError(''); setBusy(true); try { await fn(); load(); } catch (e) { setError(e.message); } finally { setBusy(false); } };

  const edit = (o) => setForm({
    id: o.id, product_id: String(o.product_id), name: o.name || '', start_date: o.start_date || '', end_date: o.end_date || '',
    is_active: !!o.is_active, choices: o.choices.map((c) => ({ product_id: String(c.product_id), offer_price: String(c.offer_price) })),
  });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setChoice = (i, k, v) => setForm((f) => ({ ...f, choices: f.choices.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const save = () => run(async () => {
    const body = { ...form, choices: form.choices.filter((c) => c.product_id).map((c) => ({ product_id: Number(c.product_id), offer_price: Number(c.offer_price || 0) })) };
    if (form.id) await api.post(`/product-offers/${form.id}`, body); else await api.post('/product-offers', body);
    setForm(null);
  });
  const toggle = (o) => run(() => api.post(`/product-offers/${o.id}`, { toggle_only: true, is_active: !o.is_active }));
  const del = (o) => { if (window.confirm(`Delete the offer on ${o.product_name}?`)) run(() => api.post(`/product-offers/${o.id}/delete`, {})); };

  return (
    <div style={S.root}>
      <div style={S.head}>
        <div>
          <h1 style={S.h1}>Product offers</h1>
          <p style={S.muted}>Buy one item, choose another free or at an offer price. In Billing, tapping the item asks which one the customer wants.</p>
        </div>
        {!form && <button type="button" style={{ ...S.btn, ...S.primary }} onClick={() => setForm({ ...EMPTY, choices: [...EMPTY.choices] })}>+ New offer</button>}
      </div>
      {error && <div style={{ marginTop: 12, padding: 12, borderRadius: 12, background: '#fdecef', color: '#d63d5b', fontWeight: 700 }}>{error}</div>}

      {form && (
        <section style={S.panel}>
          <h2 style={{ fontSize: 17, margin: '0 0 12px' }}>{form.id ? 'Edit offer' : 'New offer'}</h2>
          <div style={S.grid}>
            <label style={S.label}>When the customer buys
              <ProductSelect products={products} value={form.product_id} onChange={(v) => setF('product_id', v)} placeholder="Choose the main item" />
            </label>
            <label style={S.label}>Offer title (shown in the pop-up)
              <input style={S.input} value={form.name} onChange={(e) => setF('name', e.target.value)} placeholder="e.g. Choose one free with Cold Milo" />
            </label>
            <label style={S.label}>Starts (optional)<input type="date" style={S.input} value={form.start_date} onChange={(e) => setF('start_date', e.target.value)} /></label>
            <label style={S.label}>Ends (optional)<input type="date" style={S.input} value={form.end_date} onChange={(e) => setF('end_date', e.target.value)} /></label>
          </div>

          <h3 style={{ fontSize: 14, margin: '16px 0 8px' }}>They can choose one of</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {form.choices.map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px auto', gap: 8, alignItems: 'end' }}>
                <label style={S.label}>Item<ProductSelect products={products} value={c.product_id} onChange={(v) => setChoice(i, 'product_id', v)} placeholder="Choose an item" /></label>
                <label style={S.label}>Offer price ₹ (0 = free)<input type="number" min="0" inputMode="decimal" style={S.input} value={c.offer_price} onChange={(e) => setChoice(i, 'offer_price', e.target.value)} /></label>
                <button type="button" style={{ ...S.link, color: '#d63d5b', height: 42 }} onClick={() => setForm((f) => ({ ...f, choices: f.choices.filter((_, j) => j !== i) }))}>Remove</button>
              </div>
            ))}
          </div>
          <button type="button" style={{ ...S.link, marginTop: 8 }} onClick={() => setForm((f) => ({ ...f, choices: [...f.choices, { product_id: '', offer_price: '0' }] }))}>+ Add another choice</button>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontWeight: 700 }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setF('is_active', e.target.checked)} /> Offer is on
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button type="button" style={S.btn} onClick={() => setForm(null)}>Cancel</button>
            <button type="button" style={{ ...S.btn, ...S.primary }} disabled={busy || !form.product_id || !form.choices.some((c) => c.product_id)} onClick={save}>Save offer</button>
          </div>
        </section>
      )}

      <section style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {offers.map((o) => (
          <div key={o.id} style={S.card(o.live)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: 16 }}>🎁 {o.product_name}</strong> <span style={{ color: '#776e86' }}>{rupees(o.product_price)}</span>
                <div style={{ fontSize: 13, color: '#776e86', marginTop: 2 }}>
                  {o.name || 'Choose one'}{o.start_date || o.end_date ? ` · ${o.start_date ? niceDate(o.start_date) : '…'} to ${o.end_date ? niceDate(o.end_date) : '…'}` : ''}
                  {' · '}<b style={{ color: o.live ? '#12905f' : '#b86a00' }}>{o.live ? 'Live in Billing' : o.is_active ? 'Not in date range' : 'Off'}</b>
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                <button type="button" style={S.link} onClick={() => toggle(o)}>{o.is_active ? 'Switch off' : 'Switch on'}</button>
                <button type="button" style={S.link} onClick={() => edit(o)}>Edit</button>
                <button type="button" style={{ ...S.link, color: '#d63d5b' }} onClick={() => del(o)}>Delete</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {o.choices.map((c) => (
                <span key={c.id} style={S.chip(!(c.offer_price > 0))}>{c.product_name} · {c.offer_price > 0 ? rupees(c.offer_price) : 'FREE'}</span>
              ))}
            </div>
          </div>
        ))}
        {offers.length === 0 && !form && <div style={{ ...S.panel, textAlign: 'center', color: '#776e86' }}>No offers yet. Create one, for example Cold Milo with a free Hendy's Chicken, Veg Balls or Fries.</div>}
      </section>
    </div>
  );
}
